// @systeme: interface
// Une liste ouverte par son lien, sans compte : le conjoint au magasin, la grand-mère qui
// fait les courses. On voit, on coche, c'est tout.
import { useCallback, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import { useParams } from 'react-router';
import type { ListePartagee as Partagee } from '@foyer/shared';
import { Chargement } from '../composants/Chargement';
import { api } from '../lib/api';
import { nomRayon } from '../lib/rayons';
import { couleurs, tailles } from '../theme/theme';

export function ListePartagee() {
  const { jeton = '' } = useParams();
  const [liste, setListe] = useState<Partagee | undefined>();
  const [introuvable, setIntrouvable] = useState(false);

  const charger = useCallback(() => {
    api.partage
      .lire(jeton)
      .then(setListe)
      .catch(() => setIntrouvable(true));
  }, [jeton]);

  useEffect(() => {
    charger();
    // On rafraîchit doucement : deux personnes peuvent cocher en même temps.
    const minuterie = setInterval(charger, 15000);
    return () => clearInterval(minuterie);
  }, [charger]);

  async function cocher(articleId: string, coche: boolean) {
    try {
      setListe(await api.partage.cocher(jeton, articleId, coche));
    } catch {
      charger();
    }
  }

  if (introuvable)
    return (
      <Box sx={{ px: `${tailles.marge}px`, pt: 6, textAlign: 'center' }}>
        <Typography variant="h2" component="h1">
          Cette liste n’est plus partagée.
        </Typography>
        <Typography color="text.secondary" sx={{ mt: 1 }}>
          Demande un nouveau lien à la personne qui te l’avait envoyé.
        </Typography>
      </Box>
    );
  if (!liste) return <Chargement />;
  const aFaire = liste.articles.filter((a) => !a.coche);
  const pris = liste.articles.filter((a) => a.coche);

  return (
    <Box sx={{ maxWidth: 560, mx: 'auto', px: `${tailles.marge}px`, pt: 3, pb: 4 }}>
      <Typography variant="h1" component="h1">
        {liste.nom}
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        Liste partagée avec toi. Coche ce que tu prends.
      </Typography>
      {aFaire.length === 0 && (
        <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
          Tout est pris.
        </Typography>
      )}
      <List disablePadding>
        {aFaire.map((a) => (
          <ListItem
            key={a.id}
            disablePadding
            sx={{
              bgcolor: couleurs.blanc,
              borderRadius: `${tailles.rayon}px`,
              mb: 1,
              border: 1,
              borderColor: 'divider',
            }}
          >
            <ListItemButton onClick={() => void cocher(a.id, true)} sx={{ minHeight: 60, pl: 0.5 }}>
              <Checkbox
                checked={false}
                tabIndex={-1}
                inputProps={{ 'aria-label': `Pris : ${a.libelle}` }}
              />
              <ListItemText
                primary={a.quantite ? `${a.libelle} · ${a.quantite}` : a.libelle}
                secondary={liste.type === 'courses' ? nomRayon(a.rayon) : undefined}
                primaryTypographyProps={{ sx: { fontSize: '1.25rem' } }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      {pris.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            Déjà pris
          </Typography>
          <List disablePadding>
            {pris.map((a) => (
              <ListItem key={a.id} disablePadding>
                <ListItemButton onClick={() => void cocher(a.id, false)} sx={{ pl: 0.5 }}>
                  <Checkbox
                    checked
                    tabIndex={-1}
                    inputProps={{ 'aria-label': `Remettre : ${a.libelle}` }}
                  />
                  <ListItemText
                    primary={a.libelle}
                    primaryTypographyProps={{
                      sx: { textDecoration: 'line-through', color: 'text.secondary' },
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>
      )}
    </Box>
  );
}
